/**
 * search.ts — forgiving Hebrew name search (SPEC §1.4): partial, any word order,
 * ignores nikud, geresh/gershayim and punctuation. Phones match by digits.
 */

const NIKUD = /[֑-ׇ]/g;
const PUNCT = /["'`׳״\-–—.,()]/g;
const FINALS: Record<string, string> = { 'ך': 'כ', 'ם': 'מ', 'ן': 'נ', 'ף': 'פ', 'ץ': 'צ' };

export function normalizeHebrew(s: string): string {
  return String(s ?? '')
    .replace(NIKUD, '')
    .replace(PUNCT, ' ')
    .replace(/[ךםןףץ]/g, (c) => FINALS[c])
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function digitsOnly(s: string): string {
  return String(s ?? '').replace(/\D/g, '');
}

/**
 * Every query token must be a prefix of some word in the haystack (order does not matter).
 * "כה מש" matches "משה כהן".
 */
export function matchesQuery(query: string, haystack: string[]): boolean {
  const tokens = normalizeHebrew(query).split(' ').filter(Boolean);
  if (tokens.length === 0) return true;
  const words = haystack.flatMap((h) => normalizeHebrew(h).split(' ')).filter(Boolean);
  const qDigits = digitsOnly(query);
  if (qDigits.length >= 3 && qDigits.length === query.replace(/[\s\-+()]/g, '').length) {
    return haystack.some((h) => digitsOnly(h).includes(qDigits));
  }
  return tokens.every((t) => words.some((w) => w.startsWith(t)));
}

/** Lowercase name prefixes stored on people.searchKeys (client-maintained). */
export function searchKeysFor(...names: string[]): string[] {
  const out = new Set<string>();
  for (const n of names) {
    for (const w of normalizeHebrew(n).split(' ').filter(Boolean)) {
      for (let i = 1; i <= Math.min(w.length, 10); i++) out.add(w.slice(0, i));
    }
  }
  return [...out].sort();
}
